/**
 * The lead desk, inside Manifest Review (Isaiah Cotto, 2026-09-30), as boards
 * (2026-10-07): the same table, cells and docked record as Scheduling, Finance
 * and the UMED board.
 *
 * "No automatic distribution; that should live in the same view for Gwynn,
 * April, Dominique and Kaleigh. Because each PA has facilities they are
 * assigned to ... so leads aren't getting distributed to the wrong PAs. For
 * example Talhia is Spanish and takes the Spanish-heavy facilities."
 *
 * Two views:
 *   Leads to place     one row per RESIDENT nobody holds -- new CounselEAR
 *                      TWL + Qualified, or recorded in Manifest Review -- with
 *                      the PA who covers the facility beside it. Placing moves
 *                      every open copy of the resident together. The row opens
 *                      DOCKED beside the table: Place, This building (place
 *                      every resident at it with the covering PA), Close
 *                      instead (Disposition, R5) and Details. Nothing grows
 *                      inside the table.
 *   Facility coverage  which PA covers each building and why. Rows seeded
 *                      from history say so until somebody confirms them; the
 *                      edit is in the docked record.
 *
 * Default order is building A to Z, then resident A to Z (April Torino,
 * 2026-09-30: "it would be best if leads are alphabetized by facility name
 * first ... what we typically do is assign all leads from one facility at
 * once").
 *
 * Select-and-send-back (D-28, Dominique Genua 2026-10-02): a placer ticks leads
 * (a row, a building from its record, or everything shown) and sends the set
 * back to the provider, or closes it another way, from the board's head. A
 * lead a VA verified as qualifying (Needs verification, D-29) returns here,
 * tagged "Verified — qualifies", with who checked it and what they found.
 *
 * Older leads (Dominique Genua, 2026-10-19: the list read empty while 25
 * residents tagged 30-39 days ago waited): under the board, a section closed
 * until opened, with the same rows, the same docked record and the same Place.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  manifestApi, reasonWords, type Advocate, type BulkCoverageOutcome, type Coverage, type CoverageHolder, type CoverageList, type Lead,
} from './manifestApi';
import { DispositionAction } from './ResubmittalsDesk';
import BulkDisposition from './BulkDisposition';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import { StateBlock as TideState } from '../../components/tide';
import { verificationApi, type VerifiedLead } from './verificationApi';
import MfBoard, { useClientSort, useOpenParam, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ResidentName, residentLabel } from './board/residents';
import { ToneChip } from './board/chips';
import BuildingOrClinic, { buildingWords } from '../../components/emerge/BuildingOrClinic';

/** A bare day (a TWL Date) is read as that day, not shifted by the zone. */
const dayDate = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const FIRST_SEEN_TITLE = 'CounselEAR sends no TWL date. This is the day eMerge first saw the TWL tag on this resident. It is not a Monday TWL date.';
const longDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }) : '—');
/** A lead, plus what a VA found when this resident came back from Needs verification. */
type DeskLead = Lead & { verified?: VerifiedLead };
const byName = (a: string, b: string) => a.localeCompare(b, 'en-US', { sensitivity: 'base' });

/** Not a new lead: another episode of the resident (already sold, UMED in flight, ...). */
const notNew = (l: Lead) => Boolean(l.episode && l.episode.verdict !== 'NEW_LEAD_OK');
/**
 * Prior closure (2026-10-28): a PA already closed this resident DNQ / NITWL / C-NITWL after the manifest visit this
 * lead came from (the server's alreadyWorked rule, the same as Manifest Review's ALREADY_WORKED). Held on this desk:
 * shown, with no Place or Place all; leadDesk.place() refuses it too. This only displays. Other assignment paths
 * (the Unassigned queue, bulk assign) do not apply the hold.
 */
const priorClosure = (l: Lead) => l.prior_closure || null;
const placeable = (l: Lead) => l.workable !== false && !notNew(l) && !priorClosure(l);
const priorClosureWords = (l: Lead) => {
  const c = priorClosure(l);
  return c ? `A PA already closed this resident as ${c.word} on ${dayDate(c.closed_on)}, after this lead's manifest visit of ${dayDate(c.visit_date)}. Review that case before placing this lead.` : '';
};
/**
 * The closed case's page, only when the server says this caller can open it (`can_open_case`: ADVOCACY_LEAD_VIEW, the
 * PA workspace's gate) and sent its id; a manifest reviewer gets the words and no link.
 */
const closedCaseHref = (l: Lead, canOpenCase: boolean) => {
  const id = priorClosure(l)?.case_id;
  return canOpenCase && id ? `/portal/patient-advocate?case=${encodeURIComponent(id)}` : null;
};
const ClosedCaseLink = ({ l, canOpenCase }: { l: Lead; canOpenCase: boolean }) => {
  const href = closedCaseHref(l, canOpenCase);
  return href ? <a href={href}>Open the closed case</a> : null;
};
/** The one "Can place" chip for a lead. */
const canKey = (l: Lead): LeadKey => (l.workable === false ? 'can:finished' : priorClosure(l) ? 'can:closure' : notNew(l) ? 'can:notnew' : 'can:ready');
const finishedWords = (l: Lead) => `Only a finished case on file (${(l.finished_disposition || l.finished_stage || 'finished').replace(/_/g, ' ')}${l.finished_opened_on ? `, opened ${new Date(l.finished_opened_on).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })}` : ''}). Placing it would give the PA nothing to work. Record the resident in Manifest Review to open a new lead.`;
const holdsOf = (l: Lead) => (l.intake_exceptions || []).filter(Boolean);

/** Legend keys: where a lead came from, and whether it can be placed. */
type LeadKey = 'src:verified' | 'src:reopened' | 'src:counselear' | 'src:recorded' | 'can:ready' | 'can:notnew' | 'can:finished' | 'can:closure';
const hasKey = (l: DeskLead, k: string): boolean => {
  switch (k) {
    case 'src:verified': return Boolean(l.verified);
    case 'src:reopened': return Boolean(l.reopened_tag);
    case 'src:counselear': return Boolean(l.counselear_lead);
    case 'src:recorded': return Boolean(l.recorded);
    case 'can:finished':
    case 'can:closure':
    case 'can:notnew':
    case 'can:ready': return canKey(l) === k;
    default: return false;
  }
};

const SourceChip = ({ k, l }: { k: LeadKey; l?: DeskLead }) => {
  switch (k) {
    case 'src:verified': return <ToneChip tone="ok">Verified — qualifies</ToneChip>;
    case 'src:reopened': return <ToneChip tone="attn">{l?.reopened_tag || 'Reopened'}</ToneChip>;
    case 'src:counselear': return <ToneChip tone="ok">CounselEAR TWL + Qualified</ToneChip>;
    case 'src:recorded': return <ToneChip tone="quiet">Recorded in Manifest Review</ToneChip>;
    case 'can:ready': return <ToneChip tone="ok">Ready</ToneChip>;
    case 'can:notnew': return <ToneChip tone="attn">Not a new lead</ToneChip>;
    case 'can:closure': return <ToneChip tone="attn">Prior closure — review</ToneChip>;
    default: return <ToneChip tone="attn">Finished case only</ToneChip>;
  }
};

const SOURCE_KEYS: LeadKey[] = ['src:verified', 'src:reopened', 'src:counselear', 'src:recorded'];
const CAN_KEYS: LeadKey[] = ['can:ready', 'can:closure', 'can:notnew', 'can:finished'];
const LEAD_MEANING: Record<LeadKey, string> = {
  'src:verified': 'A VA checked this resident in Needs verification and found they qualify; the lead returned here.',
  'src:reopened': 'A manager reopened a closed lead with nobody to hold it; the tag says why.',
  'src:counselear': 'CounselEAR carries the TWL tag and the resident qualifies.',
  'src:recorded': 'Recorded in Manifest Review (a TWL day), not read from CounselEAR.',
  'can:ready': 'A new lead with a case to work: it can be placed with a PA.',
  'can:notnew': 'Another episode of this resident exists (already sold, or UMED in flight). Not placed from here.',
  'can:finished': 'Only a finished case is on file (DNQ, NITWL, enrolled). Placing it would give the PA nothing to work.',
  'can:closure': 'A PA already closed this resident DNQ, NITWL or C-NITWL after the manifest visit this lead came from. Review that case; it is not placed from here.',
};
const LEAD_LABEL: Record<LeadKey, string> = {
  'src:verified': 'Verified — qualifies', 'src:reopened': 'Reopened', 'src:counselear': 'CounselEAR TWL + Qualified', 'src:recorded': 'Recorded in Manifest Review',
  'can:ready': 'Ready', 'can:notnew': 'Not a new lead', 'can:finished': 'Finished case only',
  'can:closure': 'Prior closure — review',
};

const LEAD_SORTERS: Record<string, (l: DeskLead) => string | number | null> = {
  name: (l) => [l.first_name, l.last_name].filter(Boolean).join(' ') || null,
  // Building first, then resident (rows arrive name-ordered, so ties keep that order).
  building: (l) => (l.facility_name ? `${l.facility_name}\u0000${[l.first_name, l.last_name].filter(Boolean).join(' ')}` : null),
  twl: (l) => (l.twl_since !== undefined ? l.twl_since : l.twl_tag_since),
  pa: (l) => l.suggested_advocate,
  copies: (l) => l.open_cases,
};

/** Place one resident: choose a PA (the covering one is preselected) and place. Placing moves every open copy together. */
function PlacePanel({ lead, advocates, onPlaced, canOpenCase = false }: { lead: DeskLead; advocates: Advocate[]; onPlaced: (msg: string) => void; canOpenCase?: boolean }) {
  const [choice, setChoice] = useState<string>(lead.suggested_advocate_id || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const chosen = advocates.find((a) => a.id === choice);
  const label = residentLabel(lead);
  const place = async () => {
    if (!choice) return;
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.place(lead.case_id, choice);
      if (r.success && r.data) onPlaced(`${label} placed with ${chosen ? chosen.name : 'the PA'}${r.data.swept ? ` (and ${r.data.swept} other open ${r.data.swept === 1 ? 'copy' : 'copies'})` : ''}.`);
      else setErr(r.code === 'ASSIGNMENT_CONFLICT' ? 'Someone placed this resident since the list loaded. Refresh to see who.' : (r.message || r.error || 'Could not place this lead.'));
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };
  if (!placeable(lead)) {
    return (
      <div className="mf-panel">
        <p>{lead.workable === false
          ? 'This resident cannot be placed: only a finished case is on file.'
          : priorClosure(lead) ? priorClosureWords(lead)
            : 'This resident cannot be placed from here: they are not a new lead. If the provider saw them again for a replacement, record it on Replacements.'}</p>
        {lead.workable !== false && priorClosure(lead) && <ClosedCaseLink l={lead} canOpenCase={canOpenCase} />}
      </div>
    );
  }
  return (
    <div className="mf-panel">
      <p>{lead.open_cases > 1 ? `${lead.open_cases} open copies of this resident: placing moves them together.` : 'Placing gives this resident to the PA you choose.'}</p>
      <div className="mf-acts">
        <select aria-label={`PA for ${label}`} value={choice} onChange={(e) => setChoice(e.target.value)} disabled={busy}>
          <option value="">Choose a PA…</option>
          {advocates.map((a) => <option key={a.id} value={a.id}>{a.name}{a.manager ? ' (manager)' : ''} · {a.open_cases} open</option>)}
        </select>
        <button type="button" className="board-btn board-btn--primary" onClick={place} disabled={!choice || busy}>
          {busy ? 'Placing…' : chosen ? `Place with ${chosen.name.split(' ')[0]}` : 'Place'}
        </button>
      </div>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
    </div>
  );
}

/**
 * One building's residents: "Place all" places each in turn with the covering
 * PA -- the same placement as the Place button, one audited assignment per
 * resident -- after a confirmation. Also ticks the building for the bulk acts.
 */
function BuildingPanel({ lead, rows, advocates, selected, onSelect, onPlaced }: {
  lead: DeskLead; rows: DeskLead[]; advocates: Advocate[]; selected: Set<string>; onSelect: (ids: string[], on: boolean) => void; onPlaced: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // No building: the CounselEAR Preferred Clinic, labelled unconfirmed (2026-10-14).
  const facility = buildingWords(lead, 'Facility not on file');
  const pa = lead.suggested_advocate_id ? advocates.find((a) => a.id === lead.suggested_advocate_id) : undefined;
  const ready = rows.filter(placeable);
  const allHere = rows.length > 0 && rows.every((l) => selected.has(l.case_id));
  const placeAll = async () => {
    if (!pa || !window.confirm(`Place all ${ready.length} ${ready.length === 1 ? 'resident' : 'residents'} at ${facility} with ${pa.name}?`)) return;
    setBusy(true); setErr(null);
    const failed: string[] = [];
    let placed = 0;
    for (const l of ready) {
      try {
        const r = await manifestApi.place(l.case_id, pa.id);
        if (r.success) placed += 1; else failed.push(residentLabel(l));
      } catch { failed.push(residentLabel(l)); }
    }
    setBusy(false);
    if (failed.length) setErr(`Not placed: ${failed.join(', ')}. Refresh and place them one by one.`);
    onPlaced(`${placed} of ${ready.length} at ${facility} placed with ${pa.name}.`);
  };
  return (
    <div className="mf-panel">
      <p>{rows.length} {rows.length === 1 ? 'resident' : 'residents'} at {facility} ·{' '}
        {lead.suggested_advocate ? <>covered by <strong>{lead.suggested_advocate}</strong>{!lead.coverage_confirmed && ' (not yet confirmed)'}</> : 'no PA set'}</p>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
      <div className="mf-acts">
        {pa && ready.length > 0 && (
          <button type="button" className="board-btn board-btn--primary" onClick={placeAll} disabled={busy}>
            {busy ? 'Placing…' : `Place all ${ready.length} with ${pa.name.split(' ')[0]}`}
          </button>
        )}
        <label className="mf-check">
          <input type="checkbox" checked={allHere} onChange={() => onSelect(rows.map((l) => l.case_id), !allHere)} aria-label={`Select all in this building: ${facility}`} />
          Select all in this building
        </label>
      </div>
    </div>
  );
}

function LeadsBoard() {
  const [leads, setLeads] = useState<DeskLead[] | null>(null);
  const [older, setOlder] = useState<DeskLead[]>([]);
  const [olderShown, setOlderShown] = useState(false);
  const [olderRow, setOlderRow] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [advocates, setAdvocates] = useState<Advocate[]>([]);
  const [can, setCan] = useState(false);
  const [canOpenCase, setCanOpenCase] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [open, setOpen] = useOpenParam('open');

  const load = useCallback(async () => {
    setProblem(null);
    try {
      // The verified-and-returned list rides along; if it cannot be read the leads still show.
      const [r, v] = await Promise.all([manifestApi.leads(), verificationApi.list().catch(() => null)]);
      if (r.success && r.data) {
        const returned = v && v.success && v.data && Array.isArray(v.data.returned) ? v.data.returned : [];
        const byPatient = new Map(returned.map((x) => [x.patient_id, x]));
        const olderRaw = Array.isArray(r.data.older) ? r.data.older : [];
        const seen = new Set([...r.data.items, ...olderRaw].map((l) => l.patient_id));
        const withVerified = (l: Lead): DeskLead => (byPatient.has(l.patient_id) ? { ...l, verified: byPatient.get(l.patient_id) } : l);
        const merged: DeskLead[] = r.data.items.map(withVerified);
        const olderMerged: DeskLead[] = olderRaw.map(withVerified);
        for (const x of returned) {
          if (seen.has(x.patient_id)) continue;
          merged.push({
            patient_id: x.patient_id, first_name: x.first_name, last_name: x.last_name, facility_id: x.facility_id, facility_name: x.facility_name,
            counselear_clinic_id: x.counselear_clinic_id, counselear_clinic_name: x.counselear_clinic_name,
            twl_tag_since: x.twl_tag_since, counselear_lead: false, recorded: false, case_id: x.case_id, intake_exceptions: null, open_cases: x.open_cases,
            suggested_advocate_id: x.suggested_advocate_id, suggested_advocate: x.suggested_advocate, coverage_reason: x.coverage_reason,
            coverage_source: x.coverage_source, coverage_confirmed: x.coverage_confirmed, workable: true, verified: x,
          });
        }
        setLeads(merged); setOlder(olderMerged); setAdvocates(r.data.advocates); setCan(r.data.canPlace); setCanOpenCase(r.data.can_open_case === true);
        const here = new Set([...merged, ...olderMerged].map((l) => l.case_id));
        setSelected((prev) => new Set(Array.from(prev).filter((id) => here.has(id))));
      } else setProblem(r.message || r.error || 'Could not load the leads.');
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Residents A to Z first, so the building sort (and every tie) reads as April asked.
  const all = useMemo(() => (leads || []).slice().sort((a, b) => byName(residentLabel(a), residentLabel(b))), [leads]);
  const found = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? all.filter((l) => `${residentLabel(l)} ${buildingWords(l, '')} ${l.suggested_advocate || ''}`.toLowerCase().includes(q)) : all;
  }, [all, filter]);
  const filtered = useMemo(() => (legendKey ? found.filter((l) => hasKey(l, legendKey)) : found), [found, legendKey]);
  const { rows: shown, sort, dir, sorts, onSort } = useClientSort(filtered, LEAD_SORTERS, { sort: 'building' });

  // The older leads: the same rows, A to Z, in their own section; a closed section shows nothing, so its ticks are "not shown".
  const olderAll = useMemo(() => older.slice().sort((a, b) => byName(residentLabel(a), residentLabel(b))), [older]);
  const { rows: olderRows, sort: olderSort, dir: olderDir, sorts: olderSorts, onSort: onOlderSort } = useClientSort(olderAll, LEAD_SORTERS, { sort: 'building' });

  const shownIds = useMemo(() => shown.map((l) => l.case_id), [shown]);
  const hiddenSelected = useMemo(() => {
    const s = new Set([...shownIds, ...(olderShown ? olderRows.map((l) => l.case_id) : [])]);
    return Array.from(selected).filter((id) => !s.has(id)).length;
  }, [selected, shownIds, olderShown, olderRows]);
  const allShown = shownIds.length > 0 && shownIds.every((id) => selected.has(id));
  const select = useCallback((ids: string[], on: boolean) => setSelected((prev) => {
    const n = new Set(prev);
    for (const id of ids) { if (on) n.add(id); else n.delete(id); }
    return n;
  }), []);
  const labelFor = useCallback((id: string) => { const l = [...(leads || []), ...older].find((x) => x.case_id === id); return l ? residentLabel(l) : undefined; }, [leads, older]);
  const placed = (m: string) => { setNotice(m); load(); };

  const legendItems: MfLegendItem[] = useMemo(() => {
    const out: MfLegendItem[] = [];
    for (const k of [...SOURCE_KEYS, ...CAN_KEYS]) {
      const hits = found.filter((l) => hasKey(l, k));
      if (!hits.length) continue;
      out.push({ key: k, label: LEAD_LABEL[k], sample: <SourceChip k={k} l={hits[0]} />, meaning: LEAD_MEANING[k], count: hits.length });
    }
    return out;
  }, [found]);

  if (problem) return <main className="mf-main mf-main--board"><MfLoadFailure message={problem} retry={load} /></main>;

  const columns: BoardColumn<DeskLead>[] = [
    ...(can ? [{
      key: 'pick', label: 'Select', help: 'Pick residents to send back or close in one governed action, from the controls above the table.',
      render: (l: DeskLead) => <input type="checkbox" checked={selected.has(l.case_id)} onChange={() => select([l.case_id], !selected.has(l.case_id))} aria-label={`Select ${residentLabel(l)}`} />,
    }] : []),
    { key: 'building', label: 'Building', sort: 'building', help: 'The building the resident is in. Buildings run A to Z, residents A to Z inside each.', render: (l) => (l.facility_name ? <span className="board-clip" title={l.facility_name}>{l.facility_name}</span> : <BuildingOrClinic row={l} fallback={<span className="board-na">not on file</span>} />) },
    {
      key: 'twl', label: 'TWL since', sort: 'twl',
      help: 'The day the TWL tag was put on the resident. Where there is none (a resident made from CounselEAR, which sends no TWL date), the day eMerge first saw the TWL, labelled "first seen in CounselEAR". It is never a Monday TWL date.',
      render: (l) => {
        const when = l.twl_since !== undefined ? l.twl_since : l.twl_tag_since;
        if (!when) return <span className="board-na">not recorded</span>;
        if (l.twl_since_source === 'FIRST_SEEN') return <span className="mf-cellstack" title={FIRST_SEEN_TITLE}><span>{longDate(when)}</span><span className="board-src">first seen in CounselEAR</span></span>;
        if (l.twl_since_source === 'TWL_DATE') return <span title="The TWL Date recorded on the case.">{dayDate(when)}</span>;
        return <span>{longDate(when)}</span>;
      },
    },
    {
      key: 'source', label: 'Source', help: 'Where the lead came from: CounselEAR, Manifest Review, a VA check, or a reopened lead.',
      render: (l) => {
        const keys = SOURCE_KEYS.filter((k) => hasKey(l, k));
        return keys.length ? <span className="mf-chips">{keys.map((k) => <SourceChip key={k} k={k} l={l} />)}</span> : <span className="board-na">not recorded</span>;
      },
    },
    {
      key: 'pa', label: 'Covering PA', sort: 'pa', help: 'The PA who covers this building. A suggestion, never an assignment: the placer can choose anyone.',
      render: (l) => (l.suggested_advocate
        ? <span className="board-clip" title={l.suggested_advocate}>{l.suggested_advocate}{!l.coverage_confirmed && <span className="board-src"> (not confirmed)</span>}</span>
        : <span className="board-na">No PA set</span>),
    },
    { key: 'copies', label: 'Open copies', numeric: true, sort: 'copies', help: 'How many open copies of this resident exist. Placing moves them together.', render: (l) => <span>{l.open_cases}</span> },
    {
      key: 'holds', label: 'Holds', help: 'Why the PA\'s queue holds this resident until something is fixed.',
      render: (l) => { const h = holdsOf(l).map(reasonWords).join('; '); return h ? <span className="board-clip" title={h}>{h}</span> : <span className="board-na">none</span>; },
    },
    { key: 'can', label: 'Can place', help: 'Ready: a new lead with a case to work. Prior closure: a PA closed this resident after the visit; review that case. Not a new lead: another episode exists. Finished case only: nothing to work.', render: (l) => <SourceChip k={canKey(l)} /> },
  ];

  const identity = {
    label: 'Resident', sort: 'name',
    help: 'A resident nobody holds. Open the row for the record docked beside the table: place them, place their building, close them instead, or read the details.',
    render: (l: DeskLead) => <ResidentName p={l} />,
    name: (l: DeskLead) => residentLabel(l),
  };

  // `pool` is the list the row sits in: "this building" means the building's residents in that list.
  const sectionsIn = (pool: DeskLead[]) => (l: DeskLead): BoardDetailSection[] => {
    const holds = holdsOf(l);
    // Residents with no building are grouped by their CounselEAR clinic, not lumped together.
    const inBuilding = pool.filter((x) => (x.facility_id || 'none') === (l.facility_id || 'none') && (l.facility_id || (x.counselear_clinic_id || null) === (l.counselear_clinic_id || null)));
    const out: BoardDetailSection[] = [];
    if (can) {
      out.push({ id: 'place', label: 'Place', render: () => <PlacePanel key={l.case_id} lead={l} advocates={advocates} onPlaced={placed} canOpenCase={canOpenCase} /> });
      out.push({ id: 'building', label: 'This building', render: () => <BuildingPanel key={l.case_id} lead={l} rows={inBuilding} advocates={advocates} selected={selected} onSelect={select} onPlaced={placed} /> });
      out.push({
        id: 'close', label: 'Close instead',
        render: () => (
          <div className="mf-panel">
            <p>Close this lead with a disposition and a reason instead of placing it with a PA.</p>
            <DispositionAction caseId={l.case_id} residentLabel={residentLabel(l)} onDone={placed} />
          </div>
        ),
      });
    }
    out.push({
      id: 'details', label: 'Details',
      render: () => (
        <div className="mf-panel">
          <dl className="mf-facts">
            <dt>Covering PA</dt>
            <dd>{l.suggested_advocate
              ? <>{l.suggested_advocate}{l.coverage_reason ? ` — ${l.coverage_reason}` : ''}{!l.coverage_confirmed && ' (suggested from history, not yet confirmed)'}</>
              : 'No PA set for this facility yet'}</dd>
            {l.verified && (<><dt>Verified</dt><dd>by {l.verified.verified_by_name || 'a VA'} on {longDate(l.verified.verified_at)}: {l.verified.verified_note}</dd></>)}
            {l.episode && l.episode.verdict !== 'NEW_LEAD_OK' && (<><dt>Episode</dt><dd>{l.episode.line} — not a new lead. If the provider saw them again for a replacement, record it on Replacements.</dd></>)}
            {holds.length > 0 && (<><dt>Holds</dt><dd>Held from the PA's queue until fixed: {holds.map(reasonWords).join('; ')}</dd></>)}
            {l.workable === false && (<><dt>Finished case</dt><dd>{finishedWords(l)}</dd></>)}
            {priorClosure(l) && (<><dt>Prior closure</dt><dd>{priorClosureWords(l)} <ClosedCaseLink l={l} canOpenCase={canOpenCase} /></dd></>)}
            <dt>Open copies</dt><dd>{l.open_cases}</dd>
          </dl>
        </div>
      ),
    });
    return out;
  };
  const sections = sectionsIn(found);
  const olderSections = sectionsIn(olderAll);

  let state: React.ReactNode = null;
  if (!leads) state = <MfSkeleton bare />;
  else if (leads.length === 0) state = <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="No lead is waiting to be placed." />;
  else if (shown.length === 0) {
    state = (
      <div className="mf-panel">
        <p>No lead matches this search or filter. {leads.length} lead{leads.length === 1 ? '' : 's'} remain.</p>
        <div className="mf-acts">
          <button type="button" className="board-btn" onClick={() => { setFilter(''); setLegendKey(null); }}>Clear search and filter</button>
        </div>
      </div>
    );
  }

  const controls = leads && leads.length > 0 ? (
    <>
      <input type="search" className="mf-filter" aria-label="Find a resident, facility or PA" placeholder="Find a resident, facility or PA" value={filter} onChange={(e) => setFilter(e.target.value)} />
      {can && (
        <>
          <label className="mf-check">
            <input type="checkbox" checked={allShown} onChange={() => select(shownIds, !allShown)} aria-label={`Select all ${shownIds.length} shown`} />
            Select all {shownIds.length} shown
          </label>
          {selected.size > 0 && hiddenSelected > 0 && <span className="mf-sub">{selected.size} selected, {hiddenSelected} not shown by the filter</span>}
          <BulkDisposition count={selected.size} resolveIds={async () => Array.from(selected)} labelFor={labelFor}
            onClear={() => setSelected(new Set())}
            onFinished={async (m) => { setNotice(m); setSelected(new Set()); await load(); }} />
        </>
      )}
    </>
  ) : null;

  return (
    <main className="mf-main mf-main--board">
      <MfBoard<DeskLead>
        title="Leads to place"
        count={leads ? leads.length : null}
        units={['lead', 'leads']}
        what="One row per resident nobody holds, buildings A to Z. Open a row to place that resident, place their whole building, or close them instead. Nothing is distributed automatically."
        notice={<>
          {notice && <p className="mf-notice" role="status">{notice}</p>}
          {!can && leads && <p className="mf-notice">You can see these leads; placing them needs the lead-placement permission.</p>}
        </>}
        controls={controls}
        legend={leads && leads.length > 0 ? (
          <MfLegend title="Where leads come from, and who can be placed" items={legendItems} whole={found.length} wholeWords="in this list" units={['lead', 'leads']}
            value={legendKey} onSelect={setLegendKey}
            scope="Source chips say where a lead came from; a lead can carry several. The placement chips say whether it can be placed. Press an entry to show only those leads." />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={shown}
        rowKey={(l) => l.case_id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(l) => `${residentLabel(l)}: the lead`}
        sort={sort} dir={dir} sorts={sorts} onSort={onSort}
      />
      {older.length > 0 && (
        <section className="mf-older" aria-label="Older leads">
          <h2 className="mf-older-head">
            <button type="button" className="board-btn" aria-expanded={olderShown} aria-controls="mf-older-leads" onClick={() => setOlderShown((v) => !v)}>
              {olderShown ? '▾' : '▸'} Older leads (21–90 days) <span className="mf-board-count" data-count={older.length}>{older.length.toLocaleString('en-US')} {older.length === 1 ? 'lead' : 'leads'}</span>
            </button>
          </h2>
          {olderShown && (
            <div id="mf-older-leads">
              <MfBoard<DeskLead>
                title="Tagged 21–90 days ago"
                count={older.length}
                units={['lead', 'leads']}
                what="Tagged more than 21 days ago and still unplaced."
                columns={columns}
                identity={identity}
                rows={olderRows}
                rowKey={(l) => l.case_id}
                openKey={olderRow}
                onOpenChange={setOlderRow}
                detailSections={olderSections}
                detailLabel={(l) => `${residentLabel(l)}: the lead`}
                sort={olderSort} dir={olderDir} sorts={olderSorts} onSort={onOlderSort}
              />
            </div>
          )}
        </section>
      )}
    </main>
  );
}

/*
 * == Facility coverage (2026-10-26: "For the 42 buildings I need a way for April to assign the coverage") ==
 *
 * Every building with residents is listed, not only those with a PA or an open
 * case. The tab opens on "Nobody set" when there are any. Dropped and closed
 * buildings are hidden until "Show Dropped" is pressed: providers cannot mark
 * TWL Q there, so covering them routes nothing. Tick buildings and "Set PA for
 * N selected" sets one PA for all of them in one act: a confirm step first
 * (how many residents wait in the Unassigned queue at each building -- coverage never assigns
 * a case), then what happened to each building. A building someone else covers
 * is skipped unless Replace is ticked. The single-building edit stays in the
 * docked record.
 */
type CoverageBasis = 'confirmed' | 'suggested' | 'none';
const basisOf = (r: Coverage): CoverageBasis => (r.source === 'HISTORY' && !r.confirmed_at ? 'suggested' : r.advocate_user_id ? 'confirmed' : 'none');
/** Closed for providers (Dropped, closed for scheduling, merged): TWL Q cannot be marked there. Unknown (an older server) is shown. */
const closedForTwlQ = (r: Coverage) => r.twl_q_markable === false;
/**
 * Dropped (the closure's kind, from the business status or a DROPPED scheduling profile): what the Show Dropped
 * toggle hides and counts. A building closed another way (do not schedule, merged) stays in view, marked closed.
 */
const isDropped = (r: Coverage) => Boolean(r.twl_q_closure && r.twl_q_closure.kind === 'DROPPED');
const NO_STATUS = '(none)';
const statusOf = (r: Coverage) => r.building_status || NO_STATUS;
const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

function BasisChip({ r }: { r: Coverage }) {
  const b = basisOf(r);
  if (b === 'suggested') return <ToneChip tone="attn">Suggested from history, {Math.round(Number(r.history_share))}%</ToneChip>;
  if (b === 'confirmed') return <ToneChip tone="ok">Confirmed</ToneChip>;
  return <ToneChip tone="quiet">Nobody set</ToneChip>;
}

/** The building's business status in words, with a shape: never colour alone. */
function StatusChip({ r }: { r: Coverage }) {
  if (r.building_status === undefined) return <span className="board-na">not known</span>;
  if (closedForTwlQ(r)) return <ToneChip tone="stop" title={r.twl_q_refusal || undefined}><span aria-hidden="true">✕ </span>{r.building_status || 'Closed'}</ToneChip>;
  if (!r.building_status) return <span className="board-na">none</span>;
  const active = r.building_status.trim().toUpperCase() === 'ACTIVE';
  return <ToneChip tone={active ? 'ok' : 'quiet'}><span aria-hidden="true">{active ? '● ' : '○ '}</span>{r.building_status}</ToneChip>;
}

function TwlQChip({ r }: { r: Coverage }) {
  if (r.twl_q_markable === undefined) return <span className="board-na">not known</span>;
  return r.twl_q_markable
    ? <ToneChip tone="ok"><span aria-hidden="true">✓ </span>Can be marked</ToneChip>
    : <ToneChip tone="stop" title={r.twl_q_refusal || undefined}><span aria-hidden="true">✕ </span>Closed to TWL Q</ToneChip>;
}

const COVERAGE_SORTERS: Record<string, (r: Coverage) => string | number | null> = {
  facility: (r) => r.facility_name,
  open: (r) => r.open_cases,
  who: (r) => r.advocate,
  residents: (r) => (r.resident_count ?? null),
  unassigned: (r) => (r.open_unassigned_count ?? null),
  status: (r) => (r.building_status === undefined ? null : statusOf(r)),
};

/** The person in a picker: name, manager, and how many buildings they cover now, so the load can be balanced. */
const holderLabel = (h: CoverageHolder) => `${h.name}${h.manager ? ' (manager)' : ''} · ${plural(h.buildings, 'building', 'buildings')}${h.settable ? '' : ' · PA role not yet ratified'}`;
function HolderOptions({ holders }: { holders: CoverageHolder[] }) {
  return <>{holders.map((h) => <option key={h.id} value={h.id} disabled={!h.settable}>{holderLabel(h)}</option>)}</>;
}

/**
 * Who can be named. A server from before 2026-10-26 sends no `holders`: then the old list (PAs who are not
 * managers), with the building count read from the rows on screen.
 */
function holdersFrom(list: CoverageList, rows: Coverage[]): CoverageHolder[] {
  if (Array.isArray(list.holders)) return list.holders;
  const load = new Map<string, number>();
  for (const r of rows) if (r.advocate_user_id) load.set(r.advocate_user_id, (load.get(r.advocate_user_id) || 0) + 1);
  return list.advocates.filter((a) => !a.manager).map((a) => ({ id: a.id, name: a.name, manager: a.manager, settable: true, buildings: load.get(a.id) || 0, open_cases: a.open_cases }));
}

/** The coverage edit: who covers the building and why. Same call, same enablement as always. */
function CoveragePanel({ row, holders, onSaved }: { row: Coverage; holders: CoverageHolder[]; onSaved: (msg: string) => void }) {
  const [who, setWho] = useState(row.advocate_user_id || '');
  const [reason, setReason] = useState(row.reason || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const changed = who !== (row.advocate_user_id || '') || (reason || '') !== (row.reason || '');
  const unconfirmed = row.source === 'HISTORY' && !row.confirmed_at;
  // The current PA stays choosable even if they are no longer in the list, so opening the record changes nothing.
  const options = row.advocate_user_id && !holders.some((h) => h.id === row.advocate_user_id)
    ? [{ id: row.advocate_user_id, name: row.advocate || 'The current PA', manager: false, settable: true, buildings: 0, open_cases: 0 }, ...holders]
    : holders;
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.setCoverage(row.facility_id, who || null, reason || null);
      if (r.success) onSaved(`Coverage for ${row.facility_name} saved.`); else setErr(r.message || r.error || 'Could not save.');
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };
  return (
    <div className="mf-panel">
      <p>
        {plural(row.open_cases, 'open case', 'open cases')}
        {row.resident_count !== undefined ? `, ${plural(row.resident_count, 'resident', 'residents')}` : ''}
        {row.open_unassigned_count ? `, ${plural(row.open_unassigned_count, 'resident', 'residents')} waiting in the Unassigned queue (placed from the Unassigned tab)` : ''}.
        {unconfirmed ? ` Suggested from history (${Math.round(Number(row.history_share))}% of recent placements), not yet confirmed.` : ''}
      </p>
      {row.twl_q_refusal && <p className="mf-notice">{row.twl_q_refusal} Covering it routes nothing until the building is open again.</p>}
      <div className="mf-acts">
        <select aria-label={`PA covering ${row.facility_name}`} value={who} onChange={(e) => setWho(e.target.value)} disabled={busy}>
          <option value="">Nobody set</option>
          <HolderOptions holders={options} />
        </select>
        <input aria-label={`Why, for ${row.facility_name}`} placeholder="Why (e.g. Spanish-speaking)" value={reason} maxLength={200}
          onChange={(e) => setReason(e.target.value)} disabled={busy} />
        <button type="button" className={`board-btn${changed || unconfirmed ? ' board-btn--primary' : ''}`} onClick={save} disabled={busy || (!changed && !unconfirmed)}>
          {busy ? 'Saving…' : unconfirmed && !changed ? 'Confirm' : 'Save'}
        </button>
      </div>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
    </div>
  );
}

const OUTCOME_WORDS: Record<BulkCoverageOutcome, string> = { SET: 'Set', UNCHANGED: 'Already set', REPLACED: 'Replaced', REFUSED: 'Not changed' };
const OUTCOME_GLYPH: Record<BulkCoverageOutcome, string> = { SET: '✓', UNCHANGED: '=', REPLACED: '⇄', REFUSED: '✕' };

/** A refusal of the whole request, in words. Nothing was written. */
function bulkRefusal(r: { code?: string; status?: number }, max: number): string {
  switch (r.code) {
    case 'INVALID_FACILITIES': return 'Some selected buildings no longer exist or were merged into another building. Nothing was changed. Reload the list and try again.';
    case 'NOT_FOUND': return 'Some selected buildings are outside the buildings you may place for. Nothing was changed.';
    case 'NOT_AN_ADVOCATE': return 'That person is not a ratified Patient Advocate, so coverage cannot name them. Nothing was changed.';
    case 'PA_NOT_ACTIVE': return 'That person\'s account cannot hold PA cases now, so a TWL Q there would reach nobody. Nothing was changed.';
    case 'TOO_MANY_FACILITIES': return `At most ${max} buildings at a time. Nothing was changed.`;
    default:
      if (r.status === 403) return 'Changing coverage needs the lead-placement permission. Nothing was changed.';
      if (r.status === undefined) return 'Could not reach eMerge. Nothing was changed. Try again.';
      return 'Could not set coverage. Nothing was changed. Try again.';
  }
}

type BulkDone = { pa: string; rows: { facility_id: string; name: string; outcome: BulkCoverageOutcome; reason: string }[] };

/**
 * The sticky "Set PA for N selected" bar: choose the PA (with how many buildings each covers), an optional
 * reason, then a confirm step that shows each building's waiting Unassigned residents and who covers it now. Coverage
 * never assigns a case: they are placed from the Unassigned tab.
 */
function BulkCoverageBar({ rows, holders, max, canPlacePaWork, onClear, onDone }: {
  rows: Coverage[]; holders: CoverageHolder[]; max: number; canPlacePaWork: boolean; onClear: () => void; onDone: (done: BulkDone) => void;
}) {
  const [who, setWho] = useState('');
  const [reason, setReason] = useState('');
  const [replace, setReplace] = useState(false);
  const [phase, setPhase] = useState<'pick' | 'confirm' | 'busy'>('pick');
  const [err, setErr] = useState<string | null>(null);
  const confirmHead = useRef<HTMLHeadingElement | null>(null);
  const pa = holders.find((h) => h.id === who) || null;
  const n = rows.length;
  const others = pa ? rows.filter((r) => r.advocate_user_id && r.advocate_user_id !== pa.id) : [];
  const unassigned = rows.reduce((s, r) => s + (r.open_unassigned_count || 0), 0);
  useEffect(() => { if (phase === 'confirm' && confirmHead.current) confirmHead.current.focus(); }, [phase]);
  useEffect(() => { if (n === 0) setPhase('pick'); }, [n]);

  const submit = async () => {
    if (!pa) return;
    setPhase('busy'); setErr(null);
    const names = new Map(rows.map((r) => [r.facility_id, r.facility_name]));
    try {
      const r = await manifestApi.bulkCoverage({ facility_ids: rows.map((x) => x.facility_id), advocate_user_id: pa.id, reason: reason.trim() || null, replace });
      if (r.success && r.data) {
        onDone({ pa: r.data.advocate || pa.name, rows: r.data.results.map((x) => ({ ...x, name: names.get(x.facility_id) || 'A building' })) });
        setPhase('pick'); setReplace(false);
      } else { setErr(bulkRefusal(r, max)); setPhase('confirm'); }
    } catch { setErr(bulkRefusal({}, max)); setPhase('confirm'); }
  };

  return (
    <section className="mf-covbar" aria-label="Set PA for the selected buildings">
      {phase === 'pick' ? (
        <div className="mf-covbar-row">
          <strong className="mf-covbar-count">Set PA for {plural(n, 'selected building', 'selected buildings')}</strong>
          <select aria-label="PA for the selected buildings" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">Choose a PA…</option>
            <HolderOptions holders={holders} />
          </select>
          <input aria-label="Why (optional), for the selected buildings" placeholder="Why (optional)" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
          <button type="button" className="board-btn board-btn--primary" disabled={!pa || n > max} onClick={() => { setErr(null); setPhase('confirm'); }}>
            Review {plural(n, 'building', 'buildings')}
          </button>
          <button type="button" className="board-btn" onClick={onClear}>Clear selection</button>
          {n > max && <p className="mf-notice mf-notice--stop" role="alert">At most {max} buildings at a time. Untick {n - max} to go on.</p>}
        </div>
      ) : (
        <div className="mf-covbar-confirm">
          <h3 ref={confirmHead} tabIndex={-1}>Set {pa ? pa.name : 'the PA'} as the PA for {plural(n, 'building', 'buildings')}?</h3>
          <p className="mf-sub">
            This sets who a new lead at each building goes to. It does not assign any case:
            {unassigned
              ? <> the {plural(unassigned, 'resident', 'residents')} waiting in the Unassigned queue at these buildings stay unassigned.{' '}
                {canPlacePaWork ? <>Place them from the <Link to="?view=unassigned">Unassigned tab</Link>.</> : 'A PA manager places them from the Unassigned tab.'}</>
              : ' nobody at these buildings is waiting in the Unassigned queue.'}
          </p>
          <ul className="mf-covbar-list" aria-label="The selected buildings">
            {rows.map((r) => {
              const other = pa && r.advocate_user_id && r.advocate_user_id !== pa.id;
              const same = pa && r.advocate_user_id === pa.id;
              return (
                <li key={r.facility_id}>
                  <strong>{r.facility_name}</strong>
                  {' — '}{other ? <>covered by {r.advocate}{replace ? ', will be replaced' : ', will be skipped'}</> : same ? 'already this PA' : 'nobody set'}
                  {r.open_unassigned_count ? <> · {plural(r.open_unassigned_count, 'resident', 'residents')} in the Unassigned queue</> : <> · nobody in the Unassigned queue</>}
                  {closedForTwlQ(r) && <> · <span aria-hidden="true">✕ </span>closed to TWL Q</>}
                </li>
              );
            })}
          </ul>
          {others.length > 0 && (
            <label className="mf-check">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
              Also replace the PA on the {plural(others.length, 'building', 'buildings')} someone else covers
            </label>
          )}
          {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
          <div className="mf-acts">
            <button type="button" className="board-btn board-btn--primary" onClick={submit} disabled={phase === 'busy'}>
              {phase === 'busy' ? 'Setting…' : `Confirm: set ${pa ? pa.name.split(' ')[0] : 'PA'} for ${plural(n, 'building', 'buildings')}`}
            </button>
            <button type="button" className="board-btn" onClick={() => setPhase('pick')} disabled={phase === 'busy'}>Back</button>
          </div>
        </div>
      )}
    </section>
  );
}

function BulkResultNotice({ done, onDismiss }: { done: BulkDone; onDismiss: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, [done]);
  const counts = (o: BulkCoverageOutcome) => done.rows.filter((r) => r.outcome === o).length;
  const summary = (['SET', 'REPLACED', 'UNCHANGED', 'REFUSED'] as BulkCoverageOutcome[]).filter((o) => counts(o)).map((o) => `${OUTCOME_WORDS[o]}: ${counts(o)}`).join(' · ');
  return (
    <div className="mf-notice mf-covresult" role="status" tabIndex={-1} ref={ref}>
      <p><strong>{done.pa}</strong> — {summary}.</p>
      <ul aria-label="What happened to each building">
        {done.rows.map((r) => (
          <li key={r.facility_id} data-outcome={r.outcome}>
            <span aria-hidden="true">{OUTCOME_GLYPH[r.outcome]} </span><strong>{OUTCOME_WORDS[r.outcome]}</strong> — {r.name}: {r.reason}
          </li>
        ))}
      </ul>
      <button type="button" className="board-btn" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

function CoverageBoard({ canPlacePaWork }: { canPlacePaWork: boolean }) {
  const [list, setList] = useState<CoverageList | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [done, setDone] = useState<BulkDone | null>(null);
  const [filter, setFilter] = useState('');
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [showDropped, setShowDropped] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useOpenParam('open');
  const focused = useRef(false);

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await manifestApi.coverage();
      if (r.success && r.data) {
        const data = r.data;
        setList(data);
        // First load: a placer lands on the buildings nobody covers, when there are any.
        if (!focused.current) {
          focused.current = true;
          if (data.canEdit && data.items.some((x) => basisOf(x) === 'none')) setLegendKey('none');
        }
        const here = new Set(data.items.map((x) => x.facility_id));
        setSelected((prev) => new Set(Array.from(prev).filter((id) => here.has(id))));
      } else setProblem(r.message || r.error || 'Could not load facility coverage.');
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const coverage = list ? list.items : null;
  const can = Boolean(list && list.canEdit);
  const max = (list && list.bulkMax) || 100;
  const holders = useMemo(() => (list ? holdersFrom(list, list.items) : []), [list]);

  const found = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = coverage || [];
    return q ? rows.filter((r) => r.facility_name.toLowerCase().includes(q) || (r.advocate || '').toLowerCase().includes(q)) : rows;
  }, [coverage, filter]);
  const byStatus = useMemo(() => (status ? found.filter((r) => statusOf(r) === status) : found), [found, status]);
  // Dropped buildings are hidden until asked for: providers cannot mark TWL Q there. The count is exactly what the toggle reveals.
  const openOnes = useMemo(() => (showDropped ? byStatus : byStatus.filter((r) => !isDropped(r))), [byStatus, showDropped]);
  const filtered = useMemo(() => (legendKey ? openOnes.filter((r) => basisOf(r) === legendKey) : openOnes), [openOnes, legendKey]);
  const droppedHere = useMemo(() => byStatus.filter((r) => isDropped(r) && (!legendKey || basisOf(r) === legendKey)).length, [byStatus, legendKey]);
  const { rows: shown, sort, dir, sorts, onSort } = useClientSort(filtered, COVERAGE_SORTERS);
  const statuses = useMemo(() => {
    const s = new Set<string>();
    for (const r of found) if (r.building_status !== undefined && (showDropped || !isDropped(r))) s.add(statusOf(r));
    if (status) s.add(status);
    return Array.from(s).sort((a, b) => (a === NO_STATUS ? 1 : b === NO_STATUS ? -1 : a.localeCompare(b)));
  }, [found, showDropped, status]);
  const hasNewFields = Boolean(coverage && coverage.some((r) => r.twl_q_markable !== undefined));

  const selectable = (r: Coverage) => can && !r.merged;
  const shownIds = useMemo(() => shown.filter(selectable).map((r) => r.facility_id), [shown, can]); // eslint-disable-line react-hooks/exhaustive-deps
  const allShown = shownIds.length > 0 && shownIds.every((id) => selected.has(id));
  // Only visible ticks count: a filter that hides a ticked row clears its tick, so nothing hidden is ever submitted.
  useEffect(() => {
    const visible = new Set(shownIds);
    setSelected((prev) => (Array.from(prev).every((id) => visible.has(id)) ? prev : new Set(Array.from(prev).filter((id) => visible.has(id)))));
  }, [shownIds]);
  const select = useCallback((ids: string[], on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    for (const id of ids) { if (on) next.add(id); else next.delete(id); }
    return next;
  }), []);
  const selectedRows = useMemo(() => shown.filter((r) => selected.has(r.facility_id) && selectable(r)), [shown, selected, can]); // eslint-disable-line react-hooks/exhaustive-deps

  const legendItems: MfLegendItem[] = useMemo(() => {
    const meaning: Record<CoverageBasis, string> = {
      confirmed: 'Somebody set or confirmed this PA for the building.',
      suggested: 'Seeded from who recently received this building\'s placements; nobody has confirmed it yet.',
      none: 'No PA covers this building; a lead there suggests nobody and a provider\'s TWL Q goes to the unassigned queue.',
    };
    const label: Record<CoverageBasis, string> = { confirmed: 'Confirmed', suggested: 'Suggested from history', none: 'Nobody set' };
    const out: MfLegendItem[] = [];
    for (const b of ['confirmed', 'suggested', 'none'] as CoverageBasis[]) {
      const hits = openOnes.filter((r) => basisOf(r) === b);
      if (hits.length) out.push({ key: b, label: label[b], sample: <BasisChip r={hits[0]} />, meaning: meaning[b], count: hits.length });
    }
    return out;
  }, [openOnes]);
  const nobodyCount = openOnes.filter((r) => basisOf(r) === 'none').length;

  if (problem) return <main className="mf-main mf-main--board"><MfLoadFailure message={problem} retry={load} /></main>;

  const columns: BoardColumn<Coverage>[] = [
    ...(can ? [{
      key: 'pick', label: 'Select', help: 'Tick buildings to set one PA for all of them from the bar at the foot of the table. A merged building cannot be ticked.',
      render: (r: Coverage) => (
        <input type="checkbox" checked={selected.has(r.facility_id)} disabled={!selectable(r)} onChange={() => select([r.facility_id], !selected.has(r.facility_id))}
          aria-label={r.merged ? `${r.facility_name} was merged into another building and cannot be set` : `Select ${r.facility_name}`} />
      ),
    }] : []),
    { key: 'open', label: 'Open cases', numeric: true, sort: 'open', help: 'Open cases in the building.', render: (r) => <span>{r.open_cases}</span> },
    ...(hasNewFields ? [
      { key: 'residents', label: 'Residents', numeric: true, sort: 'residents', help: 'Residents whose building this is (merged records left out).', render: (r: Coverage) => <span>{r.resident_count ?? '—'}</span> },
      { key: 'unassigned', label: 'Unassigned', numeric: true, sort: 'unassigned', help: 'Residents waiting in the Unassigned queue at this building, counted as the Unassigned tab counts them (one per resident). Setting coverage does not assign them: they are placed from the Unassigned tab.', render: (r: Coverage) => <span>{r.open_unassigned_count ?? '—'}</span> },
    ] : []),
    { key: 'who', label: 'Covered by', sort: 'who', help: 'The PA a lead from this building suggests, and who a provider\'s TWL Q goes to. The placer can always choose someone else.', render: (r) => (r.advocate ? <span className="board-clip" title={r.advocate}>{r.advocate}</span> : <span className="board-na">nobody</span>) },
    { key: 'basis', label: 'Basis', help: 'Confirmed: somebody set it. Suggested from history: seeded from recent placements, share shown. Nobody set.', render: (r) => <BasisChip r={r} /> },
    ...(hasNewFields ? [
      { key: 'status', label: 'Status', sort: 'status', help: 'The building\'s business status as stored (Active, Cold, Interested, DONT BUG, Dropped). ✕ marks a building closed to providers.', render: (r: Coverage) => <StatusChip r={r} /> },
      { key: 'twlq', label: 'TWL Q', help: 'Whether a provider could mark TWL Q here today. Not in a Dropped, closed or merged building.', render: (r: Coverage) => <TwlQChip r={r} /> },
    ] : []),
    { key: 'why', label: 'Why', help: 'The reason the PA covers the building, as the placer wrote it.', render: (r) => (r.reason ? <span className="board-clip" title={r.reason}>{r.reason}</span> : <span className="board-na">not recorded</span>) },
  ];
  const identity = {
    label: 'Facility', sort: 'facility',
    help: 'A building. Open the row to set which PA covers it and why.',
    render: (r: Coverage) => <strong>{r.facility_name}</strong>,
    name: (r: Coverage) => r.facility_name,
  };
  const sections = (r: Coverage): BoardDetailSection[] => (can
    ? [{ id: 'coverage', label: 'Coverage', render: () => <CoveragePanel key={r.facility_id} row={r} holders={holders} onSaved={(m) => { setNotice(m); load(); }} /> }]
    : [{
      id: 'coverage', label: 'Coverage',
      render: () => (
        <div className="mf-panel">
          <p>{r.advocate ? `Covered by ${r.advocate}.` : 'Nobody is set for this building.'} Changing coverage needs the lead-placement permission.</p>
          {r.twl_q_refusal && <p className="mf-notice">{r.twl_q_refusal}</p>}
        </div>
      ),
    }]);

  const covered = (coverage || []).filter((r) => r.advocate_user_id).length;
  const clearAll = () => { setFilter(''); setLegendKey(null); setStatus(''); };
  let state: React.ReactNode = null;
  if (!coverage) state = <MfSkeleton bare />;
  else if (coverage.length === 0) state = <TideState kind="empty" reason="none" message="No facility is listed." />;
  else if (shown.length === 0) {
    state = (
      <div className="mf-panel">
        <p>
          {legendKey === 'none' && !filter && !status ? 'Every open building in this list has a PA.' : 'No facility matches this search or filter.'}
          {droppedHere > 0 && !showDropped ? ` ${plural(droppedHere, 'Dropped building is', 'Dropped buildings are')} hidden.` : ''}
        </p>
        <div className="mf-acts">
          <button type="button" className="board-btn" onClick={clearAll}>Clear search and filter</button>
          {droppedHere > 0 && !showDropped && <button type="button" className="board-btn" onClick={() => setShowDropped(true)}>Show Dropped ({droppedHere})</button>}
        </div>
      </div>
    );
  }

  const controls = coverage && coverage.length > 0 ? (
    <>
      <input type="search" className="mf-filter" aria-label="Find a facility or PA" placeholder="Find a facility or PA" value={filter} onChange={(e) => setFilter(e.target.value)} />
      {hasNewFields && (
        <>
          <div className="mf-chipbar" role="group" aria-label="Which buildings">
            <button type="button" className={`board-btn${legendKey === 'none' ? ' board-btn--primary' : ''}`} aria-pressed={legendKey === 'none'} onClick={() => setLegendKey(legendKey === 'none' ? null : 'none')}>
              Nobody set <span className="mf-count">{nobodyCount}</span>
            </button>
            <button type="button" className={`board-btn${legendKey === null ? ' board-btn--primary' : ''}`} aria-pressed={legendKey === null} onClick={() => setLegendKey(null)}>
              All buildings <span className="mf-count">{openOnes.length}</span>
            </button>
          </div>
          <label className="mf-check">
            <span>Status</span>
            <select aria-label="Building status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {statuses.map((s) => <option key={s} value={s}>{s === NO_STATUS ? 'No status' : s}</option>)}
            </select>
          </label>
          <button type="button" className="board-btn" aria-pressed={showDropped} onClick={() => setShowDropped((v) => !v)}>
            {showDropped ? `Hide Dropped (${droppedHere})` : `Show Dropped (${droppedHere})`}
          </button>
        </>
      )}
      {can && shownIds.length > 0 && (
        <label className="mf-check">
          <input type="checkbox" checked={allShown} onChange={() => select(shownIds, !allShown)} aria-label={`Select all ${shownIds.length} shown`} />
          Select all {shownIds.length} shown
        </label>
      )}
    </>
  ) : null;

  return (
    <main className="mf-main mf-main--board">
      <MfBoard<Coverage>
        title="Facility coverage"
        count={coverage ? coverage.length : null}
        units={['facility', 'facilities']}
        what={`Which PA covers each facility${coverage ? ` (${covered} of ${coverage.length} covered)` : ''}. A provider's TWL Q at a building goes to its PA; a lead suggests that PA, and the placer can always choose someone else. Open a row to change one, or tick several and set one PA for all of them.`}
        notice={<>
          {notice && <p className="mf-notice" role="status">{notice}</p>}
          {done && <BulkResultNotice done={done} onDismiss={() => setDone(null)} />}
          {hasNewFields && !showDropped && droppedHere > 0 && (
            <p className="mf-notice">{plural(droppedHere, 'Dropped building is', 'Dropped buildings are')} hidden: providers cannot mark TWL Q in a Dropped building, so covering it routes nothing. Press Show Dropped to see them.</p>
          )}
        </>}
        controls={controls}
        legend={coverage && coverage.length > 0 ? (
          <MfLegend title="How each building's PA was set" items={legendItems} whole={openOnes.length} wholeWords="in this list" units={['facility', 'facilities']} exclusive
            value={legendKey} onSelect={setLegendKey}
            scope="Every facility is in exactly one of these. Press an entry to show only those facilities." />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={shown}
        rowKey={(r) => r.facility_id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(r) => `${r.facility_name}: coverage`}
        sort={sort} dir={dir} sorts={sorts} onSort={onSort}
      />
      {can && selectedRows.length > 0 && (
        <BulkCoverageBar rows={selectedRows} holders={holders} max={max} canPlacePaWork={canPlacePaWork} onClear={() => setSelected(new Set())}
          onDone={(d) => { setDone(d); setNotice(null); setSelected(new Set()); load(); }} />
      )}
    </main>
  );
}


/**
 * `canPlacePaWork`: whether the caller can open the Unassigned tab (ManifestShell's own test). Only then does the
 * coverage tab link to it; a reviewer who cannot place PA work would be bounced back to the manifests.
 */
export default function LeadDesk({ view, canPlacePaWork = false }: { view: 'leads' | 'coverage'; canPlacePaWork?: boolean }) {
  return view === 'leads' ? <LeadsBoard /> : <CoverageBoard canPlacePaWork={canPlacePaWork} />;
}
